import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { CreditCard, ArrowLeft, RefreshCw, PauseCircle } from "lucide-react";
import { API_BASE as API } from "../utils/api";

const fmt = (n) => `RD$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TYPES = { personal_loan: ["Personal", "Personal"], salary_advance: ["Adelanto de salario", "Salary advance"], emergency_loan: ["Emergencia", "Emergency"], education_loan: ["Educativo", "Education"] };
const STATUS = { active: ["Activo", "Active"], pending: ["Pendiente", "Pending"], paid: ["Pagado", "Paid"], paid_off: ["Pagado", "Paid"], forgiven: ["Perdonado", "Forgiven"], cancelled: ["Cancelado", "Cancelled"] };

// Employee self-service: own loans, balances and payments.
export default function MyLoansPage() {
  const { language } = useLanguage();
  const isEs = language === "es";
  const i = isEs ? 0 : 1;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/people-ops/me/loans`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div className="max-w-md mx-auto p-10 text-center space-y-3" data-testid="my-loans-no-profile">
        <CreditCard className="w-10 h-10 mx-auto text-muted-foreground opacity-60" />
        <p className="text-muted-foreground">{isEs ? "Tu usuario no está vinculado a una ficha de empleado. Pide a Recursos Humanos que lo vincule." : "Your user isn't linked to an employee profile. Ask HR to link it."}</p>
        <Link to="/dashboard" className="text-sm underline">{isEs ? "Volver al inicio" : "Back to home"}</Link>
      </div>
    );
  }
  if (!data) return <div className="p-10 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="my-loans-page">
      <Link to="/dashboard" className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-4 h-4" />{isEs ? "Volver" : "Back"}</Link>
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><CreditCard className="w-7 h-7" />{isEs ? "Mis préstamos" : "My loans"}</h1>
        <p className="text-muted-foreground mt-1">{data.employee_name}</p>
      </div>
      <Card className="border-blue-200 bg-blue-50/40">
        <CardContent className="pt-5">
          <p className="text-sm text-blue-700">{isEs ? "Saldo total pendiente" : "Total outstanding"}</p>
          <p className="text-3xl font-bold text-blue-700" data-testid="my-loans-total">{fmt(data.total_balance)}</p>
        </CardContent>
      </Card>
      {data.loans.length === 0 ? (
        <p className="text-center text-muted-foreground py-10">{isEs ? "No tienes préstamos." : "You have no loans."}</p>
      ) : data.loans.map((l) => {
        const approved = Number(l.approved_amount || l.requested_amount || 0);
        const pct = approved ? Math.min(100, ((approved - Number(l.remaining_balance || 0)) / approved) * 100) : 0;
        const pays = data.payments.filter((p) => p.loan_id === l.loan_id).slice(0, 5);
        return (
          <Card key={l.loan_id} data-testid={`my-loan-${l.loan_id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{(TYPES[l.loan_type] || ["Préstamo", "Loan"])[i]}</span>
                <span className="flex gap-1">
                  {l.paused && <Badge variant="outline" className="border-amber-300 text-amber-700"><PauseCircle className="w-3 h-3 mr-1" />{isEs ? "Pausado" : "Paused"}</Badge>}
                  <Badge variant="secondary">{(STATUS[l.status] || [l.status, l.status])[i]}</Badge>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">{isEs ? "Cuota" : "Installment"}</p><p className="font-semibold">{fmt(l.installment_amount)}</p></div>
                <div><p className="text-xs text-muted-foreground">{isEs ? "Cuotas" : "Installments"}</p><p className="font-semibold">{l.installments_paid || 0} / {l.installments}</p></div>
                <div><p className="text-xs text-muted-foreground">{isEs ? "Saldo" : "Balance"}</p><p className="font-semibold text-blue-700">{fmt(l.remaining_balance)}</p></div>
              </div>
              <Progress value={pct} className="h-2" />
              {pays.length > 0 && (
                <ul className="text-xs text-muted-foreground space-y-0.5">
                  {pays.map((p) => <li key={p.payment_id} className="flex justify-between"><span>{String(p.payment_date || p.created_at || "").slice(0, 10)}</span><span>-{fmt(p.amount)}</span></li>)}
                </ul>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
